import { useMessagingLive } from "../hooks/useMessagingLive";
import { MessageFiles, MessageUpload } from "../components/MessageFiles";
import type { MessageFile } from "../api/messaging.types";
import { useRef, useState, type FormEvent } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { http } from "../api/http";
import { useAuthStore } from "../store/auth";
import TeamView from "./TeamView";
import type { Conversation, Directory, Message } from "../api/messaging.types";
import {
  isRefusal,
  studentMessagingApi,
  studentMessagingError,
  studentMessagingKeys,
} from "../api/studentMessaging";
import ConversationList from "../components/ConversationList";
import NewMessageForm, { EMPTY_DRAFT } from "../components/NewMessageForm";
import { OFFLINE } from "../components/StudentMessageParts";
import StudentMessageThread from "../components/StudentMessageThread";
import {
  countUnread,
  shownStudentConversations,
  useStudentConversations,
} from "../hooks/useStudentConversations";
import { useShareTeamUnread } from "../hooks/useTeamUnread";
import { mergeConversations } from "../lib/unifiedMessaging";
const root = "/v1/messaging";
const button =
  "rounded-xl bg-indigo-600 text-white px-4 py-2 disabled:opacity-50";
export default function MessagesView() {
  useMessagingLive();
  const [files, setFiles] = useState<MessageFile[]>([]),
    [uploading, setUploading] = useState(false);
  const [search, setSearch] = useState<{
    q?: string;
    authorId?: string;
    entityType?: string;
    entityId?: string;
  }>({});
  const user = useAuthStore((s) => s.user),
    qc = useQueryClient();
  const [selected, setSelected] = useState<string | null>(null),
    [text, setText] = useState(""),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [creating, setCreating] = useState(false),
    [draft, setDraft] = useState(EMPTY_DRAFT),
    // Conversation avec un ou des élèves ouverte : une autre messagerie, un autre fil.
    [student, setStudent] = useState<string | null>(null),
    [before, setBefore] = useState<number | undefined>(),
    [editing, setEditing] = useState<Message | null>(null),
    [remove, setRemove] = useState<Message | null>(null),
    [object, setObject] = useState<{
      kind: "notes" | "meetings" | "boards";
      id: string;
    } | null>(null),
    [references, setReferences] = useState<
      { entityType: string; entityId: string }[]
    >([]),
    [link, setLink] = useState("");
  const pending = useRef({ signature: "", requestId: "" }),
    lock = useRef(false);
  const list = useQuery({
    queryKey: ["messaging", user?.id],
    queryFn: async () =>
      (
        await http.get<{ items: Conversation[]; unread: number }>(
          root + "/conversations",
        )
      ).data,
    refetchInterval: (q) => (q.state.error ? false : 5000),
    retry: false,
    gcTime: 0,
  });
  const directory = useQuery({
    queryKey: ["messaging-directory", user?.id],
    queryFn: async () => (await http.get<Directory>(root + "/directory")).data,
    retry: false,
    gcTime: 0,
  });
  const messages = useQuery({
    queryKey: ["messaging-messages", user?.id, selected, before, search],
    queryFn: async () =>
      (
        await http.get<{ items: Message[]; hasMore: boolean }>(
          `${root}/conversations/${selected}/messages`,
          { params: { before, ...search } },
        )
      ).data,
    enabled: !!selected,
    refetchInterval: (q) => (q.state.error ? false : 5000),
    retry: false,
    gcTime: 0,
  });
  const last = messages.data?.items[messages.data.items.length - 1]?.sequence;
  // Les échanges avec les élèves viennent d'une autre messagerie : leur panne
  // ou leur refus ne doit rien retirer à celle de l'équipe.
  const students = useStudentConversations();
  const contacts = useQuery({
    queryKey: studentMessagingKeys.contacts(user?.id),
    queryFn: studentMessagingApi.getContacts,
    staleTime: 0,
    retry: false,
    gcTime: 0,
  });
  const teamDown = list.isError || directory.isError;
  // La pastille de la navigation reprend le total que cet écran affiche.
  useShareTeamUnread(teamDown ? 0 : list.data?.unread);
  async function command(path: string, payload: object, patch = false) {
    if (lock.current) return false;
    lock.current = true;
    setBusy(true);
    setError("");
    const signature = JSON.stringify([path, payload, patch]);
    if (pending.current.signature !== signature)
      pending.current = { signature, requestId: crypto.randomUUID() };
    try {
      await http[patch ? "patch" : "post"](root + path, {
        ...payload,
        requestId: pending.current.requestId,
      });
      pending.current = { signature: "", requestId: "" };
      await qc.invalidateQueries({ queryKey: ["messaging"] });
      await qc.invalidateQueries({ queryKey: ["messaging-messages"] });
      return true;
    } catch {
      setError(
        "Opération non confirmée. Actualisez et réessayez ; votre saisie est conservée.",
      );
      return false;
    } finally {
      lock.current = false;
      setBusy(false);
    }
  }
  async function send(e: FormEvent) {
    e.preventDefault();
    const content = {
      bodyVersion: 1,
      segments: [
        { kind: "text", text },
        ...references.map((reference) => ({ kind: "reference", reference })),
      ],
    };
    if (
      await command(
        `/conversations/${selected}/messages${editing ? "/" + editing.id : ""}`,
        {
          content,
          ...(editing
            ? { version: editing.version }
            : { fileIds: files.map((f) => f.id) }),
        },
        !!editing,
      )
    ) {
      setText("");
      setFiles([]);
      setReferences([]);
      setEditing(null);
      setBefore(undefined);
    }
  }
  function addLink() {
    try {
      const url = new URL(link);
      if (
        ![
          "permio.fr",
          "www.permio.fr",
          "app.permio.fr",
          window.location.hostname,
        ].includes(url.hostname) ||
        url.pathname !== "/workspace/reference"
      )
        throw new Error();
      const entityType = url.searchParams.get("type"),
        entityId = url.searchParams.get("id");
      if (
        !entityType ||
        !entityId ||
        !/^\w+$/.test(entityType) ||
        !/^[a-f0-9-]{36}$/i.test(entityId)
      )
        throw new Error();
      setReferences((r) =>
        r.some((v) => v.entityId === entityId && v.entityType === entityType)
          ? r
          : [...r, { entityId, entityType }],
      );
      setLink("");
    } catch {
      setError(
        "Collez un lien métier Permio. Son accès sera vérifié avant envoi.",
      );
    }
  }
  if (object)
    return (
      <section>
        <button className={button} onClick={() => setObject(null)}>
          Retour aux messages
        </button>
        <TeamView initial={object} />
      </section>
    );
  const studentItems = shownStudentConversations(students);
  // Refus du serveur : rien n'est proposé. Panne passagère : ce qui est déjà connu reste utilisable.
  const access = isRefusal(contacts.error) ? undefined : contacts.data;
  const studentAccess =
    access?.canOpenDirect || access?.canCreateGroup ? access : undefined;
  const studentFailure = students.isError
    ? students.error
    : contacts.isError
      ? contacts.error
      : null;
  const studentsReachable = studentItems.length > 0 || !!studentAccess;
  const retryStudents = () => {
    void students.refetch();
    void contacts.refetch();
  };
  if (student) {
    const listed = studentItems.find((c) => c.id === student);
    return (
      <section>
        <h1 className="sr-only">Messages</h1>
        <StudentMessageThread
          key={student}
          conversationId={student}
          fallbackTitle={listed?.title}
          fallbackKind={listed?.kind}
          onBack={() => setStudent(null)}
        />
      </section>
    );
  }
  const conversations = mergeConversations(
    teamDown ? [] : (list.data?.items ?? []),
    studentItems,
  );
  // Une seule liste et un seul « Nouveau message » pour les deux messageries.
  const overview = (
    <>
      <p>
        {(teamDown ? 0 : (list.data?.unread ?? 0)) + countUnread(studentItems)}{" "}
        message(s) non lu(s)
      </p>
      <button className={button} onClick={() => setCreating(!creating)}>
        Nouveau message
      </button>
      {creating && (
        <NewMessageForm
          draft={draft}
          onDraft={setDraft}
          me={user?.id}
          directory={teamDown ? undefined : directory.data}
          refreshing={directory.isFetching}
          onRefresh={() => {
            void directory.refetch();
            if (studentAccess) void contacts.refetch();
          }}
          busy={busy}
          studentAccess={studentAccess}
          onCreateTeam={(conversation) =>
            command("/conversations", conversation)
          }
          onTeamCreated={() => setCreating(false)}
          onStudentConversation={(id) => {
            void qc.invalidateQueries({
              queryKey: studentMessagingKeys.conversations(user?.id),
            });
            setCreating(false);
            setStudent(id);
          }}
        />
      )}
      <ConversationList
        items={conversations}
        onOpen={(c) =>
          c.origin === "student" ? setStudent(c.id) : setSelected(c.id)
        }
      />
      {conversations.length === 0 &&
        (teamDown || !!list.data) &&
        !students.isPending && <p>Aucune conversation.</p>}
      {!teamDown && studentFailure && (
        <p role="status" className="text-sm text-slate-500">
          Échanges avec les élèves :{" "}
          {studentMessagingError(studentFailure, OFFLINE)}{" "}
          <button type="button" className="underline" onClick={retryStudents}>
            Réessayer
          </button>
        </p>
      )}
    </>
  );
  return (
    <section className="rounded-2xl border bg-white p-6 space-y-4">
      <h1 className="text-xl font-bold">Messages</h1>
      {teamDown ? (
        <>
          <p role="alert">
            {studentsReachable
              ? "Messagerie de l’équipe indisponible ou accès retiré. Les échanges avec les élèves restent accessibles."
              : "Messagerie indisponible ou accès retiré."}
          </p>
          <button
            className={button}
            onClick={() => {
              void list.refetch();
              void directory.refetch();
              if (studentFailure) retryStudents();
            }}
          >
            Réessayer
          </button>
          {studentsReachable && overview}
        </>
      ) : selected ? (
        <>
          <button
            className={button}
            onClick={() => {
              setSearch({});
              setFiles([]);
              setSelected(null);
              setBefore(undefined);
              setText("");
              setEditing(null);
              setReferences([]);
            }}
          >
            Conversations
          </button>
          {messages.isError ? (
            <>
              <p role="alert">
                Conversation indisponible. Le contenu est masqué.
              </p>
              <button
                className={button}
                onClick={() => void messages.refetch()}
              >
                Réessayer
              </button>
            </>
          ) : (
            <>
              <form
                className="space-y-2 rounded-xl border p-3"
                onSubmit={(e) => {
                  e.preventDefault();
                  const f = new FormData(e.currentTarget);
                  const url = String(f.get("reference") || "").trim();
                  let ref = {};
                  if (url) {
                    try {
                      const u = new URL(url);
                      if (
                        ![
                          "permio.fr",
                          "www.permio.fr",
                          "app.permio.fr",
                          window.location.hostname,
                        ].includes(u.hostname) ||
                        u.pathname !== "/workspace/reference"
                      )
                        throw new Error();
                      ref = {
                        entityType: u.searchParams.get("type") || "",
                        entityId: u.searchParams.get("id") || "",
                      };
                    } catch {
                      setError("Lien métier invalide.");
                      return;
                    }
                  }
                  setSearch({
                    q: String(f.get("q") || "").trim() || undefined,
                    authorId: String(f.get("author") || "") || undefined,
                    ...ref,
                  });
                  setBefore(undefined);
                }}
              >
                <label>
                  Rechercher dans la conversation
                  <input
                    name="q"
                    minLength={2}
                    maxLength={120}
                    className="block border rounded-xl p-2"
                  />
                </label>
                <label>
                  Auteur
                  <select name="author" className="block border rounded-xl p-2">
                    <option value="">Tous les auteurs</option>
                    {directory.data?.items.map((u) => (
                      <option key={u.id} value={u.id}>
                        {u.firstName} {u.lastName}
                      </option>
                    ))}
                  </select>
                </label>
                <label>
                  Lien de l’objet recherché (facultatif)
                  <input
                    name="reference"
                    className="block border rounded-xl p-2"
                  />
                </label>
                <button className={button}>Rechercher l’historique</button>
                <button
                  type="button"
                  onClick={() => {
                    setSearch({});
                    setBefore(undefined);
                  }}
                >
                  Effacer la recherche
                </button>
              </form>
              {!before && !Object.values(search).some(Boolean) && !!last && (
                <button
                  className={button}
                  onClick={() =>
                    void http
                      .post(`${root}/conversations/${selected}/read`, {
                        sequence: last,
                      })
                      .then(() =>
                        qc.invalidateQueries({ queryKey: ["messaging"] }),
                      )
                      .catch(() => setError("Lecture non confirmée."))
                  }
                >
                  Marquer comme lu
                </button>
              )}
              {before && (
                <button className={button} onClick={() => setBefore(undefined)}>
                  Derniers messages
                </button>
              )}
              {messages.data?.hasMore && (
                <button
                  className={button}
                  onClick={() => setBefore(messages.data!.items[0].sequence)}
                >
                  Messages précédents
                </button>
              )}
              <ol className="space-y-3">
                {messages.data?.items.map((m) => (
                  <li
                    key={m.id}
                    className="rounded-xl border bg-slate-50 p-3 space-y-2"
                  >
                    <strong>{m.authorName}</strong>
                    <small className="block">
                      {new Date(m.createdAt).toLocaleString("fr-FR")}
                      {m.editedAt ? " · Modifié" : ""}
                    </small>
                    {m.deletedAt ? (
                      <p>Message supprimé</p>
                    ) : (
                      <>
                        <p className="whitespace-pre-wrap">
                          {m.content?.segments
                            .flatMap((s) => (s.kind === "text" ? [s.text] : []))
                            .join("\n")}
                        </p>
                        <MessageFiles
                          conversation={selected}
                          files={m.files ?? []}
                          onError={setError}
                        />
                        {m.references.map((r, i) => {
                          const kind = (
                            {
                              NOTE: "notes",
                              MEETING: "meetings",
                              BOARD: "boards",
                            } as const
                          )[r.entityType as "NOTE" | "MEETING" | "BOARD"];
                          return (
                            <div className="rounded-xl border p-2" key={i}>
                              {r.available ? (
                                <>
                                  <strong>{r.label}</strong>
                                  {r.fields?.map((f) => (
                                    <p key={f.label}>
                                      {f.label} : {f.value}
                                    </p>
                                  ))}
                                  {kind && (
                                    <button
                                      className="text-indigo-600 underline"
                                      onClick={() =>
                                        setObject({ kind, id: r.entityId })
                                      }
                                    >
                                      Ouvrir l’objet
                                    </button>
                                  )}
                                </>
                              ) : (
                                "Élément Permio · Vous n’avez pas accès à cet élément."
                              )}
                            </div>
                          );
                        })}
                        <div className="flex gap-2 flex-wrap">
                          {["👍", "✅", "👀"].map((emoji) => (
                            <button
                              key={emoji}
                              disabled={busy}
                              aria-label={"Réagir " + emoji}
                              aria-pressed={m.reactions.some(
                                (r) => r.emoji === emoji && r.mine,
                              )}
                              onClick={() =>
                                void command(
                                  `/conversations/${selected}/messages/${m.id}/reactions`,
                                  {
                                    emoji,
                                    active: !m.reactions.some(
                                      (r) => r.emoji === emoji && r.mine,
                                    ),
                                  },
                                )
                              }
                            >
                              {emoji}{" "}
                              {m.reactions.find((r) => r.emoji === emoji)
                                ?.count || ""}
                            </button>
                          ))}
                          {m.authorId === user?.id && (
                            <>
                              {Date.now() - Date.parse(m.createdAt) <
                                900000 && (
                                <button
                                  onClick={() => {
                                    setFiles([]);
                                    setEditing(m);
                                    setText(
                                      m
                                        .content!.segments.flatMap((s) =>
                                          s.kind === "text" ? [s.text] : [],
                                        )
                                        .join("\n"),
                                    );
                                    setReferences(
                                      m.content!.segments.flatMap((s) =>
                                        s.kind === "reference"
                                          ? [s.reference]
                                          : [],
                                      ),
                                    );
                                  }}
                                >
                                  Modifier
                                </button>
                              )}
                              <button onClick={() => setRemove(m)}>
                                Supprimer
                              </button>
                            </>
                          )}
                        </div>
                      </>
                    )}
                  </li>
                ))}
              </ol>
              {remove && (
                <div>
                  <p>
                    Masquer le message ? Une trace historique est conservée.
                  </p>
                  <button
                    disabled={busy}
                    className={button}
                    onClick={() =>
                      void command(
                        `/conversations/${selected}/messages/${remove.id}/delete`,
                        { version: remove.version },
                      ).then((ok) => {
                        if (ok) setRemove(null);
                      })
                    }
                  >
                    Confirmer la suppression
                  </button>
                  <button onClick={() => setRemove(null)}>Annuler</button>
                </div>
              )}
              <form onSubmit={send} className="space-y-2">
                <label className="block">
                  Votre message
                  <textarea
                    className="block w-full rounded-xl border p-3"
                    required={
                      !references.length &&
                      !files.length &&
                      !editing?.files?.length
                    }
                    maxLength={20000}
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    disabled={busy}
                  />
                </label>
                {!editing && (
                  <MessageUpload
                    conversation={selected}
                    files={files}
                    onFiles={setFiles}
                    onBusy={setUploading}
                    onError={setError}
                    disabled={busy}
                  />
                )}
                {references.map((r, i) => (
                  <div key={i}>
                    Objet métier · {r.entityType}
                    <button
                      type="button"
                      onClick={() =>
                        setReferences(references.filter((_, n) => n !== i))
                      }
                    >
                      Retirer
                    </button>
                  </div>
                ))}
                <label className="block">
                  Lien métier Permio
                  <input
                    className="block w-full rounded-xl border p-2"
                    value={link}
                    onChange={(e) => setLink(e.target.value)}
                  />
                </label>
                <button
                  type="button"
                  disabled={!link || references.length >= 50 || busy}
                  onClick={addLink}
                >
                  Joindre l’objet
                </button>
                <button
                  className={button}
                  disabled={
                    busy ||
                    uploading ||
                    (!text.trim() &&
                      !references.length &&
                      !files.length &&
                      !editing?.files?.length)
                  }
                >
                  {editing ? "Enregistrer le message" : "Envoyer"}
                </button>
                {editing && (
                  <button
                    type="button"
                    onClick={() => {
                      setEditing(null);
                      setText("");
                      setReferences([]);
                    }}
                  >
                    Annuler
                  </button>
                )}
              </form>
            </>
          )}
        </>
      ) : (
        overview
      )}
      {error && <p role="alert">{error}</p>}
    </section>
  );
}
